import React, { useState, useEffect, useCallback } from 'react';
import { api, formatApiError } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Plus, Trash, ListChecks, PencilSimple, Eye, ClipboardText } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { EditorRow, FormRowToolbar, parseTextToRows } from '../components/form-templates/FormBuilderParts';
import { PreviewDocument } from '../components/form-templates/FormPreview';

export default function FormTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const emptyForm = { name: '', description: '', fields: [] };
  const [form, setForm] = useState(emptyForm);
  const [pasteMode, setPasteMode] = useState(false);
  const [pasteText, setPasteText] = useState('');

  const fetchTemplates = useCallback(async () => {
    try { const { data } = await api.get('/form-templates'); setTemplates(data); }
    catch (error) { toast.error(formatApiError(error)); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const addRow = (type) => {
    const id = `field_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    let newField = { id, type };
    if (type === 'checkbox_row') newField.label = '';
    if (type === 'dropdown_row') { newField.label = ''; newField.options = ''; }
    setForm({ ...form, fields: [...form.fields, newField] });
  };

  const updateField = (index, updated) => {
    const newFields = [...form.fields]; newFields[index] = updated;
    setForm({ ...form, fields: newFields });
  };

  const removeField = (index) => setForm({ ...form, fields: form.fields.filter((_, i) => i !== index) });

  const moveField = (from, to) => {
    const newFields = [...form.fields]; const [moved] = newFields.splice(from, 1);
    newFields.splice(to, 0, moved); setForm({ ...form, fields: newFields });
  };

  const handlePasteImport = () => {
    if (!pasteText.trim()) { toast.error('Paste some text first'); return; }
    const newRows = parseTextToRows(pasteText);
    if (newRows.length === 0) { toast.error('Could not detect any rows from the text'); return; }
    setForm({ ...form, fields: [...form.fields, ...newRows] });
    setPasteText(''); setPasteMode(false);
    toast.success(`${newRows.length} rows added to form`);
  };

  const fieldsForApi = (fields) => {
    const typeMap = { 'section': 'heading', 'text_row': 'text_input', 'long_text_row': 'long_text', 'date_row': 'date_input', 'yes_no_row': 'yes_no', 'checkbox_row': 'checklist', 'dropdown_row': 'dropdown', 'note': 'paragraph' };
    return fields.map(f => {
      const field = { ...f };
      if (typeMap[field.type]) field.type = typeMap[field.type];
      if (typeof field.options === 'string' && (f.type === 'dropdown_row' || f.type === 'dropdown')) {
        field.optionsRaw = field.options;
        field.options = field.options.split(',').map(o => o.trim()).filter(Boolean);
      }
      if ((f.type === 'checkbox_row' || f.type === 'checklist') && !field.items) field.items = [field.label || ''];
      return field;
    });
  };

  const fieldsFromApi = (fields) => {
    const reverseMap = { 'heading': 'section', 'text_input': 'text_row', 'long_text': 'long_text_row', 'date_input': 'date_row', 'yes_no': 'yes_no_row', 'checklist': 'checkbox_row', 'dropdown': 'dropdown_row', 'paragraph': 'note', 'number_input': 'text_row', 'email_input': 'text_row', 'phone_input': 'text_row' };
    return (fields || []).map(f => ({ ...f, type: reverseMap[f.type] || f.type, options: f.optionsRaw || (Array.isArray(f.options) ? f.options.join(', ') : (f.options || '')) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Form name is required'); return; }
    if (form.fields.length === 0) { toast.error('Add at least one row'); return; }
    setSaving(true);
    const payload = { name: form.name, description: form.description, fields: fieldsForApi(form.fields) };
    try {
      if (editingId) {
        const { data } = await api.put(`/form-templates/${editingId}`, payload);
        setTemplates(templates.map(t => t.id === editingId ? data : t)); toast.success('Form updated');
      } else {
        const { data } = await api.post('/form-templates', payload);
        setTemplates([data, ...templates]); toast.success('Form created');
      }
      setDialogOpen(false); setForm(emptyForm); setEditingId(null);
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setSaving(false); }
  };

  const handleEdit = (template) => {
    setForm({ name: template.name, description: template.description || '', fields: fieldsFromApi(template.fields) });
    setEditingId(template.id); setDialogOpen(true);
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Delete this form template?')) return;
    try { await api.delete(`/form-templates/${templateId}`); setTemplates(templates.filter(t => t.id !== templateId)); toast.success('Form template deleted'); }
    catch (error) { toast.error(formatApiError(error)); }
  };

  const countInputFields = (fields) => (fields || []).filter(f => !['heading', 'paragraph', 'section', 'note'].includes(f.type)).length;

  return (
    <div className="space-y-6" data-testid="form-templates-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="heading-2 mb-1">Form Templates</h1>
          <p className="text-muted-foreground">Create document-style forms for clients to fill in</p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setEditingId(null); setDialogOpen(true); }} data-testid="create-form-template-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Form
        </Button>
      </div>

      {/* Form List */}
      <Card className="border border-border">
        <CardHeader className="pb-4">
          <CardTitle className="heading-4 flex items-center gap-2"><ListChecks className="w-5 h-5" /> Your Form Templates</CardTitle>
          <CardDescription>Reusable forms you can assign to any client</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12"><ListChecks className="w-10 h-10 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No form templates yet</p></div>
          ) : (
            <Table>
              <TableHeader><TableRow className="table-header"><TableHead>Form Name</TableHead><TableHead>Description</TableHead><TableHead>Fields</TableHead><TableHead>Created</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id} data-testid={`form-template-row-${template.id}`}>
                    <TableCell className="font-medium">{template.name}</TableCell>
                    <TableCell className="text-muted-foreground max-w-xs truncate">{template.description || '-'}</TableCell>
                    <TableCell><Badge variant="outline">{countInputFields(template.fields)} fields</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{new Date(template.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setPreviewTemplate(template); setPreviewOpen(true); }} title="Preview"><Eye className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(template)} title="Edit"><PencilSimple className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(template.id)} title="Delete"><Trash className="w-4 h-4 text-destructive" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Form Builder Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setForm(emptyForm); setEditingId(null); } }}>
        <DialogContent className="max-w-5xl max-h-[94vh] overflow-y-auto p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle className="text-lg">{editingId ? 'Edit' : 'Create'} Form Template</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1"><Label>Form Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Self Assessment Checklist 2025/26" className="text-base" data-testid="form-name-input" /></div>
              <div className="space-y-1"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Brief description" className="text-base" data-testid="form-description-input" /></div>
            </div>

            <div className="bg-gray-100 rounded-lg p-4 sm:p-6">
              <div className="bg-white border-2 border-gray-400 shadow max-w-[800px] mx-auto" data-testid="document-editor">
                <div className="border-b-2 border-gray-400 px-6 py-5 text-center">
                  <h2 className="font-bold text-xl text-gray-900">{form.name || 'Form Title'}</h2>
                  {form.description && <p className="text-sm text-gray-500 mt-1">{form.description}</p>}
                  <p className="text-xs text-gray-400 mt-3">Applegrow Financial Advisors</p>
                </div>
                {form.fields.length === 0 ? (
                  <div className="text-center py-12 text-gray-400"><ClipboardText className="w-10 h-10 mx-auto mb-3 opacity-40" /><p className="text-base">Your form is empty</p><p className="text-sm mt-1">Paste your document below to auto-create rows, or add rows manually</p></div>
                ) : (
                  <table className="w-full border-collapse" data-testid="form-table"><tbody>
                    {form.fields.map((field, idx) => (
                      <EditorRow key={field.id} field={field} index={idx} total={form.fields.length} onChange={(updated) => updateField(idx, updated)} onRemove={removeField} onMove={moveField} />
                    ))}
                  </tbody></table>
                )}
              </div>
            </div>

            <FormRowToolbar pasteMode={pasteMode} setPasteMode={setPasteMode} pasteText={pasteText} setPasteText={setPasteText} onPasteImport={handlePasteImport} onAddRow={addRow} />

            <div className="flex justify-end gap-3 pt-3 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="save-form-template-btn">{saving ? 'Saving...' : editingId ? 'Update Form' : 'Create Form'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5" /> Form Preview — How the client will see it</DialogTitle>
          </DialogHeader>
          {previewTemplate && <div className="px-6 pb-6"><PreviewDocument template={previewTemplate} /></div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
